import { AdvancedSearchServiceCaseRequest } from "@coherehealth/qm-api";

// All date fields must use the LOCAL calendar date, not the UTC date. split("T")[0] extracts
// the UTC date which for users east of UTC (e.g. IST UTC+5:30) is one day behind the date
// they selected (IST midnight May 14 = UTC May 13 18:30 -> split gives "2026-05-13").
//   dateCreatedAfter  -> startOfDay Eastern: using local date means the window starts at the
//                        correct local day rather than the day before (slightly wider is fine).
//   caseCompletedBefore -> endOfDay Eastern: sending one day behind EXCLUDES all cases
//                          completed on the selected date for users east of UTC.
// Guard at each call site is a no-op when already date-only (.includes("T") check).
// Returns iso unchanged on an invalid Date to avoid propagating "NaN-NaN-NaN";
// DatePicker only emits valid ISO strings in practice so this path is purely defensive.
// Invariant: DatePicker emits local-midnight ISO (new Date(value).toJSON()), not UTC midnight.
// So for any timezone: a Pacific user picking May 15 emits "2026-05-15T07:00:00.000Z" and
// getDate() returns 15. A UTC user picking May 15 emits "2026-05-15T00:00:00.000Z" and
// getDate() returns 15. IST midnight emit is "2026-05-14T18:30:00.000Z" and getDate()=15.
//
// NOTE: the BE filters caseCompletedBefore on caseCompletedTimeStampAsLong, but the column
// displays outcome.dateCompleted; these can differ. See searchColumns.ts workCompleted comment.
function isoToLocalDateOnly(iso: string): string {
  if (!iso.includes("T")) {
    return iso; // already date-only; new Date() parses these as UTC midnight, not local
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return iso;
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function buildSearchRequest(flatParams: Record<string, unknown>): AdvancedSearchServiceCaseRequest {
  const { memberName, memberId, memberDob, caseIdOrTrackingNumber, ...rest } = flatParams;

  const request = { ...rest } as AdvancedSearchServiceCaseRequest;

  if (memberName || memberId || memberDob) {
    const nameVal = Array.isArray(memberName)
      ? (memberName[0] as string | undefined)
      : (memberName as string | undefined);
    const idVal = Array.isArray(memberId) ? (memberId[0] as string | undefined) : (memberId as string | undefined);
    let dobVal = memberDob as string | undefined;

    if (dobVal) {
      dobVal = isoToLocalDateOnly(dobVal);
    }

    request.memberInfo = {
      ...(nameVal ? { name: nameVal } : {}),
      ...(idVal ? { id: idVal } : {}),
      ...(dobVal ? { dob: dobVal } : {}),
    };
  }

  const vals = caseIdOrTrackingNumber as string[] | undefined;
  if (vals && vals.length > 0) {
    request.caseNumberList = vals;
    request.trackingNumberList = vals;
  }

  if (request.dateCreatedAfter && typeof request.dateCreatedAfter === "string") {
    request.dateCreatedAfter = isoToLocalDateOnly(request.dateCreatedAfter);
  }
  if (request.caseCompletedBefore && typeof request.caseCompletedBefore === "string") {
    request.caseCompletedBefore = isoToLocalDateOnly(request.caseCompletedBefore);
  }

  return request;
}
